import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  BERECHNUNGSNAME,
  EINHEITSNAME,
  TARIFNAME,
  auswahlAendern,
  auswahlAnlegen,
  fassungVerwerfen,
  nachfolgerAnlegen,
  tabelle as ladeTabelle,
  tabelleAendern,
  tabelleAnlegen,
  tabelleKopieren,
  tabelleLoeschen,
  wirksamAusSaetzen,
  zuschlagAnlegen,
  zuschlagLoeschen,
  satzLoeschen,
  satzSchliessen,
  type Berechnung,
  type Fassungszeile,
  type Honorarsatzzeile,
  type Honorartabelle as Tabelle,
  type Honorartabellenzeile,
  type Honorarzuschlagzeile,
  type Leistungseinheit,
} from '../api/honorare';
import { alsEurosatz, alsProzent } from '../betrag';
import { liste as ressourcenliste } from '../api/ressourcen';
import { ApiFehler } from '../api/fehler';
import { alsTag } from '../datum';
import { Meldung } from '../komponenten/Meldung';
import { Planung } from '../komponenten/Planung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Auswahllistenpflege } from './Auswahllistenpflege';
import { Honorargitter, Stichtagswahl } from './Honorargitter';

/**
 * Die Honorartabelle (RES-68 bis RES-73).
 *
 * ============================================================================
 * DIE TABELLE HAT EINEN STICHTAG UND FASSUNGEN (12.09.2026)
 * ============================================================================
 *
 * Auftraggeber: „Die honorarsätze sollen konfigurierbar sein und wie die
 * Preislisten für die Produkte mit einem Datum versehen werden, um später
 * anpassbar und nachvollziehbar zu sein."
 *
 * Das Datum stand schon an jedem Satz. Gefehlt haben zwei Dinge, und beide waren
 * Sache dieser Maske:
 *
 *   DER STICHTAG. Das Gitter zeigte immer nur heute. Eine Fassung ab Januar ließ
 *   sich schreiben und nicht ansehen — bis Januar. Jetzt steht über dem Gitter
 *   „Sätze zum", und damit ist eine vorbereitete Fassung prüfbar, bevor sie gilt.
 *
 *   DIE ÜBERSICHT. „Alle Fassungen zeigen" listete sechsundsiebzig Zellen mal
 *   ihre Fassungen, nach Kategorie sortiert — dieselbe Auskunft wie bei den
 *   Preislisten, aber in der Form, die bei den Preislisten die Preiszeilen haben.
 *   Was dort die Liste der Preislisten ist, ist hier „Fassungen dieser Tabelle":
 *   ein Tag, ein Name, die Zahl der Sätze.
 *
 * EINE FASSUNG IST KEIN EIGENES OBJEKT in der Datenbank — sie ist ein `gueltigAb`
 * mit einem Namen. Warum kein Gefäß wie `fach.preisliste`, steht in Migration
 * 0190. Für die Maske macht das keinen Unterschied: Sie zeigt eine Liste,
 * springt auf einen Tag und verwirft eine ganze Fassung in einem Griff.
 *
 * ============================================================================
 * WARUM ES DIESE MASKE GEBEN MUSS
 * ============================================================================
 *
 * RES-68 verlangt eine Honorartabelle je Leistungsart mit Gültigkeitszeitraum.
 * Die Tabellen stehen seit Migration 0183 im Bestand; änderbar waren sie damit
 * nur per SQL — und das ist Programmierung. Erst mit dieser Maske ist die
 * Anforderung erfüllt. Dieselbe Überlegung wie bei den Gebührenstaffeln
 * (BUC-45).
 *
 * ============================================================================
 * JE FAHRTRAINING EINE ZEILE, DAZU DIE BEIDEN STUNDENZEILEN
 * ============================================================================
 *
 * Auftraggeber am 12.09.2026: „erstelle für jedes produkt fahrtraining einen
 * honorartabelleneintrag für wochentag und wochenende, dann einen honorareintrag
 * für stundensatz (nicht in abhängigkeit zum training) und einen honorareintrag
 * für koordinator. alle anderen honorareinträge entferne, genauso
 * zusatzleistungen, bzw. zuschläge."
 *
 * Das ist die Form, die diese Maske zeigt: ein Gitter mit einer Zeile je
 * Fahrtraining, zwei Stundenzeilen und zwei Spalten. Die Zeilenachse hat an
 * einem Tag drei Fassungen gehabt — sechs Beispiele aus RES-68 (0183), vier
 * Trainingstypen (0186), fünf plus zwei Stundenzeilen (0187) —, und diese ist
 * die erste, die nicht zu grob ist: Ein Drift-Training und ein Basis-Training
 * sind beide „Pkw" und kosten nicht dasselbe.
 *
 * WEIL DIE KATEGORIE DAS TRAINING IST, kann sie fehlen. Der Hinweis oben in
 * dieser Maske ist deshalb kein Beiwerk: Er ist die einzige Stelle, an der ein
 * Training ohne Honorareintrag auffällt.
 *
 * ============================================================================
 * ES GIBT NUR EINE HONORARTABELLE (12.09.2026)
 * ============================================================================
 *
 * Auftraggeber: „Wieso gibt es zwei honorartabellen? Einmal Honorarsätze und
 * einmal trainingstypen."
 *
 * Zu Recht. Unter dem Gitter stand eine zweite Tabelle, die alle Kategorien noch
 * einmal auflistete — sinnvoll, solange die Kategorien eigenständig waren, und
 * ein Duplikat, seit sie aus den Produkten kommen. Schlimmer: Ihr „Umbenennen"
 * hätte das Ladewerkzeug beim nächsten Katalogzugang überschrieben.
 *
 * Was von ihr blieb, steht jetzt AM GITTER: „Außer Gebrauch setzen" je Zeile,
 * der Weg zurück als eine Zeile darunter, und das Anlegen einer Kategorie OHNE
 * Training — der eine Fall, den das Gitter nicht von sich aus abdeckt.
 *
 * Was es NICHT mehr gibt, ist das Umbenennen einer Kategorie. Bei einem
 * Fahrtraining kommt der Name vom Produkt; ihn hier zu ändern war eine Handlung,
 * die beim nächsten Lauf des Ladewerkzeugs verschwand.
 *
 * PFLEGBAR bleibt alles andere: die Beträge im Gitter, die Zuschlagsarten und die
 * Gründe. Und ein Schlüssel lässt sich nach wie vor nicht umbenennen — er
 * verbindet Sätze und Aufträge.
 *
 * ============================================================================
 * SIE IST AM TAG IHRER AUSLIEFERUNG LEER, UND DAS IST ABSICHT
 * ============================================================================
 *
 * Die Zeilen stehen, die Beträge nicht. Ein Honorarsatz ist eine vertragliche
 * Zusage des Hauses; wer sie erfindet, erfindet sie für den ersten Auftrag, der
 * darauf zugreift.
 *
 * Die Maske sagt das, statt eine leere Tabelle als Fehler aussehen zu lassen.
 *
 * ============================================================================
 * EIN SATZ WIRD FORTGESCHRIEBEN UND NICHT GEÄNDERT (RES-72)
 * ============================================================================
 *
 * Im Gitter stehen Eingabefelder, und trotzdem wird kein Betrag überschrieben:
 * Der Server schließt den geltenden Satz zum Vortag und legt den neuen daneben.
 * Die Begründung steht am Gitter selbst; hier steht, was folgt — „Frühere
 * Fassungen" zeigt jede Fassung, die es je gab, und die Handlungen daran sind
 * dieselben wie vorher.
 *
 * ============================================================================
 * NETTO
 * ============================================================================
 *
 * Alle Beträge sind Nettobeträge. Der externe Trainer schreibt seine eigene
 * Rechnung (RES-76) und weist darauf aus, was für ihn gilt — 19 Prozent, 7
 * Prozent oder als Kleinunternehmer gar nichts. Das ist seine Angelegenheit
 * und keine Eigenschaft des Satzes.
 */

/** Ein externer Trainer in der Auswahl beim Kopieren (0192). */
type Trainerwahl = { readonly id: string; readonly bezeichnung: string };

/**
 * Die externen Trainer — Ziel einer Kopie (0192, RES-69).
 *
 * NUR DIE EXTERNEN. Ein interner Trainer hat einen Arbeitsvertrag und kein
 * Honorar; `honorartabelle_nur_extern` weist eine Tabelle an ihm ab (AeA-21).
 * Ihn zur Wahl zu stellen hieße, in eine Ablehnung zu führen.
 */
async function ladeTrainer(): Promise<readonly Trainerwahl[]> {
  const zeilen = await ressourcenliste({ arten: ['trainer'] });
  return zeilen
    .filter((r) => r.zugehoerigkeit === 'extern')
    .map((r) => ({ id: r.id, bezeichnung: r.bezeichnung }));
}

/** Heute als JJJJ-MM-TT. */
function heute(): string {
  const jetzt = new Date();
  return [
    String(jetzt.getFullYear()),
    String(jetzt.getMonth() + 1).padStart(2, '0'),
    String(jetzt.getDate()).padStart(2, '0'),
  ].join('-');
}

/** Der Tag davor — „gültig bis" beim Schließen eines Satzes. */
function tagDavor(datum: string): string {
  const gelesen = new Date(`${datum}T12:00:00Z`);
  gelesen.setUTCDate(gelesen.getUTCDate() - 1);
  return gelesen.toISOString().slice(0, 10);
}

/**
 * Die Einheiten, die eine Kategorie tragen kann (0183).
 *
 * Die Vorbelegung ist `stunde`: Wer hier von Hand anlegt, legt etwas an, das kein
 * Fahrtraining ist — und das ist heute in beiden Fällen eine Stundenzeile.
 */
const EINHEITEN: readonly Leistungseinheit[] = [
  'stunde',
  'einsatz',
  'tag',
  'halbtag',
  'block',
  'kilometer',
  'nacht',
];

/**
 * Der Entwurf einer Honorartabelle (0191).
 *
 * `art` unterscheidet die drei Wege, die es gibt: eine neue anlegen, eine
 * bestehende ändern, oder eine NACHFOLGERIN — die letzte ist ein eigener Weg,
 * weil sie die Vorgängerin im selben Griff beendet.
 */
type Gefaessentwurf = {
  readonly art: 'neu' | 'aendern' | 'nachfolger' | 'kopie';
  /** Bei „ändern", „nachfolger" und „kopie": welche Tabelle gemeint ist. */
  readonly id: string;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly gueltigAb: string;
  readonly gueltigBis: string;
  readonly bemerkung: string;
  /** Nur bei „nachfolger": die Beträge der Vorgängerin mitnehmen. */
  readonly uebernehmen: boolean;
  /**
   * Nur bei „kopie": für wen sie gilt (0192).
   *
   * Beim NACHFOLGER ist das keine Frage — er behält die Stufe der Vorgängerin.
   * Beim Kopieren ist es die eigentliche: „die Haussätze für Herrn Behrens" ist
   * der Fall, um den es geht.
   */
  readonly ressource: string | null;
};

type Kategorieentwurf = {
  readonly schluessel: string;
  readonly bezeichnung: string;
  readonly einheit: Leistungseinheit;
};

type Zuschlagentwurf = {
  readonly leistungsart: string;
  readonly zuschlagsart: string;
  readonly berechnung: Berechnung;
  readonly wert: string;
  readonly gueltigAb: string;
};

/**
 * Welche Löschung im Pop-up nachgefragt wird (Auftrag CRM-104).
 *
 * Die ganze Zeile und nicht nur ihre Kennung: Das Pop-up nennt, was verloren
 * geht — Betrag, Tag, Zahl der Sätze —, und es soll das auch dann noch können,
 * wenn die Liste dahinter inzwischen neu geladen ist.
 */
type Nachfrage =
  | { readonly art: 'satz'; readonly satz: Honorarsatzzeile }
  | { readonly art: 'fassung'; readonly fassung: Fassungszeile; readonly tabelle: string }
  | { readonly art: 'tabelle'; readonly tabelle: Honorartabellenzeile }
  | { readonly art: 'zuschlag'; readonly zuschlag: Honorarzuschlagzeile };

export function Honorartabelle({
  darfAendern,
}: {
  /**
   * RES-73: `artikel:honorar_aendern`. Ohne es ist die Maske eine Ansicht —
   * Werkzeugleiste, Gitter und Fassungen bleiben, jede Handlung fehlt. Wer
   * Honorare gar nicht sehen darf (`honorar_sehen`), kommt nicht hierher.
   */
  readonly darfAendern: boolean;
}): JSX.Element {
  const [stand, setzeStand] = useState<Tabelle | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laedt, setzeLaedt] = useState(true);
  /**
   * Der Tag, für den das Gitter die Sätze zeigt (0190).
   *
   * Er wird NICHT an den Server geschickt: `GET /honorare/tabelle` liefert alle
   * Fassungen, und `wirksamAusSaetzen` wählt daraus. Damit kostet ein Sprung ins
   * nächste Jahr keinen Umlauf — und die Maske bleibt zu jedem Datum bedienbar,
   * auch zu einem, für das es nichts gibt.
   */
  const [stichtag, setzeStichtag] = useState(heute());
  /**
   * Welches GEFÄSS die Sätze unten zeigen (0191).
   *
   * `null` heißt: die, die der Server wählt — die allgemeine, die am Geschäftstag
   * gilt. Erst wenn der Anwender eine andere anklickt, steht hier eine.
   */
  const [gewaehlt, setzeGewaehlt] = useState<string | null>(null);
  /** Die Pflege einer Honorartabelle: neu, ändern, oder eine Nachfolgerin. */
  const [gefaess, setzeGefaess] = useState<Gefaessentwurf | null>(null);
  const [auchBeendete, setzeAuchBeendete] = useState(false);
  /**
   * Die externen Trainer — nur für das Kopieren geladen (0192).
   *
   * NICHT BEIM ÖFFNEN DER MASKE: Neunundneunzig von hundert Aufrufen pflegen
   * Beträge und brauchen die Liste nie. Ein Umlauf, der fast immer umsonst ist,
   * ist ein Umlauf zu viel.
   */
  const [trainer, setzeTrainer] = useState<readonly Trainerwahl[] | null>(null);
  /** Die Zellenliste unter der Fassungsübersicht — eingeklappt. */
  const [zellen, setzeZellen] = useState(false);
  const [eigene, setzeEigene] = useState<Kategorieentwurf | null>(null);
  const [zuschlag, setzeZuschlag] = useState<Zuschlagentwurf | null>(null);
  /** Das Gitter wird bearbeitet — die Werkzeugleiste hält Tabelle und Stichtag fest. */
  const [gitterOffen, setzeGitterOffen] = useState(false);
  /** Die Löschung, die gerade im Pop-up nachgefragt wird (CRM-104). */
  const [nachfrage, setzeNachfrage] = useState<Nachfrage | null>(null);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      setzeStand(await ladeTabelle(gewaehlt ?? undefined));
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [gewaehlt]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /**
   * Ein Handgriff, der neu lädt und die Meldung stehen lässt.
   *
   * Die Fehlermeldung wird NICHT gelöscht, wenn das Nachladen danach gelingt —
   * dieselbe Falle wie im Trainerkalender: Der Anwender sähe den Fehler nie,
   * weil die Maske ihn sich selbst wegräumt.
   */
  const handgriff = useCallback(async (tun: () => Promise<unknown>, sagen: string) => {
    setzeLaedt(true);
    setzeFehler(null);
    setzeMeldung(null);
    try {
      await tun();
      setzeMeldung(sagen);
      setzeStand(await ladeTabelle(gewaehlt ?? undefined));
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [gewaehlt]);

  const anpacken = useCallback(
    (tun: () => Promise<unknown>, sagen: string) => {
      void handgriff(tun, sagen);
    },
    [handgriff],
  );

  /**
   * Derselbe Handgriff für die Rückfrage im Pop-up (CRM-104) — er WIRFT.
   *
   * Ein Fehler beim Schreiben gehört ins Pop-up, das dabei offen bleibt; über
   * der Maske stünde er ein zweites Mal. Erst der Erfolg schließt es. Scheitert
   * danach nur das Nachladen, ist die Sache geschehen, und `holen` legt den
   * Fehler auf die Seite.
   */
  const schreibe = useCallback(
    async (tun: () => Promise<unknown>, sagen: string) => {
      setzeFehler(null);
      setzeMeldung(null);
      await tun();
      setzeNachfrage(null);
      setzeMeldung(sagen);
      await holen();
    },
    [holen],
  );

  /**
   * Die Sätze einer anderen Honorartabelle zeigen — aus der Werkzeugleiste
   * und aus der Zeile der Tabellenliste derselbe Weg.
   */
  const zeigeTabelle = (id: string, gueltigAb: string): void => {
    setzeMeldung(null);
    setzeGewaehlt(id);
    /*
      DER STICHTAG SPRINGT MIT. Eine Tabelle, die 2027 beginnt, zeigt zum
      heutigen Stichtag nichts — und das sähe aus, als wäre sie leer. Der
      Beginn ist der Tag, zu dem man sie ansehen will.
    */
    setzeStichtag(gueltigAb > heute() ? gueltigAb : heute());
  };

  if (stand === null) {
    return (
      <Planung oben={fehler !== null ? <Meldung fehler={fehler} /> : undefined}>
        {fehler === null && <p role="status">Wird geladen …</p>}
      </Planung>
    );
  }

  const leer = stand.saetze.length === 0 && stand.zuschlaege.length === 0;
  const aktiveArten = stand.leistungsarten.filter((a) => a.aktiv);
  const stillgelegt = stand.leistungsarten.filter((a) => !a.aktiv);
  /** Die Zeile des GEFÄSSES, dessen Sätze das Gitter zeigt (0191). */
  const gefaessZeile = stand.tabellen.find((t) => t.id === stand.gewaehlt);
  const sichtbareTabellen = auchBeendete
    ? stand.tabellen
    : stand.tabellen.filter((t) => t.gueltigBis === null || t.gueltigBis >= heute());

  /*
   * DIE WERKZEUGLEISTE (ÄA-41, Seitentyp Planung): welche Tabelle, zu welchem
   * Tag, in welcher Fassung. Alle drei waren vorher über die Karten verteilt;
   * die Knöpfe in den Tabellen darunter bleiben — sie sind der Weg von der
   * Zeile aus, die Leiste der von oben.
   */
  const werkzeuge = (
    <>
      <label className="liste__filter">
        Honorartabelle{' '}
        <select
          aria-label="Honorartabelle"
          value={stand.gewaehlt ?? ''}
          disabled={laedt || gitterOffen}
          onChange={(e) => {
            const t = stand.tabellen.find((z) => z.id === e.target.value);
            if (t !== undefined) zeigeTabelle(t.id, t.gueltigAb);
          }}
        >
          {stand.gewaehlt === null && <option value="">keine gewählt</option>}
          {stand.tabellen
            .filter((t) => t.geloeschtAm === null || t.id === stand.gewaehlt)
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.kennung} · {t.ressource === null ? 'allgemein' : (t.ressourceName ?? 'ein Trainer')}
              </option>
            ))}
        </select>
      </label>
      <Stichtagswahl stichtag={stichtag} beiStichtag={setzeStichtag} gesperrt={gitterOffen} />
      {stand.fassungen.length > 0 && (
        <label className="liste__filter">
          Fassung{' '}
          <select
            aria-label="Fassung"
            value={stand.fassungen.some((f) => f.gueltigAb === stichtag) ? stichtag : ''}
            disabled={gitterOffen}
            onChange={(e) => {
              if (e.target.value === '') return;
              setzeMeldung(null);
              setzeStichtag(e.target.value);
            }}
          >
            <option value="">zum Stichtag</option>
            {stand.fassungen.map((f) => (
              <option key={f.gueltigAb} value={f.gueltigAb}>
                ab {f.gueltigAb}
                {f.bezeichnung === null ? '' : ` · ${f.bezeichnung}`}
              </option>
            ))}
          </select>
        </label>
      )}
    </>
  );

  return (
    <Planung
      werkzeuge={werkzeuge}
      oben={
        <>
          <p className="hilfe">
            Alle Beträge sind <strong>netto</strong>. Der externe Trainer stellt seine eigene
            Rechnung und weist die Steuer selbst aus. Die Sätze hier sind die{' '}
            <strong>allgemeine Vorgabe</strong>; ein eigener Satz am Trainer hat Vorrang und
            steht in seiner Ressourcenakte.
          </p>

          {leer && (
            <div className="hinweiskarte hinweiskarte--warnung">
              <p>
                Es ist noch kein Honorarsatz hinterlegt. Die Kategorien und die Tarife stehen,
                die Beträge nicht — sie sind eine vertragliche Zusage des Hauses und wurden
                bewusst nicht vorbelegt.
              </p>
              {/* RES-70 */}
              <p>
                Solange nichts hinterlegt ist, verlangt jede Anfrage eine ausdrückliche Angabe mit
                Grund („Kein Tabellensatz hinterlegt").
              </p>
            </div>
          )}

          {fehler !== null && <Meldung fehler={fehler} />}
          {meldung !== null && (
            <p className="hinweiszeile" role="status">
              {meldung}
            </p>
          )}

          {/* ==================================================================
              DIE LÜCKE: EIN FAHRTRAINING OHNE HONORAREINTRAG (0188)
              ==================================================================

              Seit dem 12.09.2026 IST die Kategorie das Training. Damit entsteht mit
              jedem neuen Training eine Lücke — und in einer Tabelle mit
              sechsunddreißig Zeilen fällt eine fehlende nicht auf.

              Der Knopf daneben ist der Grund, warum dieser Hinweis mehr ist als
              eine Mahnung: Er legt den Eintrag an, mit der Artikelnummer als
              Schlüssel und dem Produktnamen als Bezeichnung. Ohne ihn wäre der
              einzige Weg ein Skript auf dem Server. */}
          {stand.ohneEintragGesamt > 0 && (
            <div className="hinweiskarte hinweiskarte--warnung">
              <p>
                <strong>
                  {stand.ohneEintragGesamt === 1
                    ? 'Ein Fahrtraining hat noch keinen Honorareintrag'
                    : `${String(stand.ohneEintragGesamt)} Fahrtrainings haben noch keinen Honorareintrag`}.
                </strong>{' '}
                Solange keiner steht, verlangt jede Anfrage zu diesem Training eine
                ausdrückliche Angabe mit Grund.
              </p>
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle" aria-label="Fahrtrainings ohne Honorareintrag">
                  <tbody>
                    {stand.ohneEintrag.map((t) => (
                      <tr key={t.artikel}>
                        <th scope="row">
                          {t.bezeichnung}
                          <br />
                          <span className="hilfe">{t.artikelnummer}</span>
                        </th>
                        {darfAendern && (
                          <td>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              disabled={laedt}
                              onClick={() => {
                                anpacken(
                                  () =>
                                    auswahlAnlegen('leistungsarten', {
                                      /*
                                        Die Artikelnummer als Schlüssel: lesbar, stabil
                                        und schon im richtigen Muster (Großbuchstaben,
                                        Ziffern, Bindestrich). Die Verbindung trägt
                                        trotzdem `artikel` — der Schlüssel ist nur ein
                                        Name.
                                      */
                                      schluessel: t.artikelnummer.toUpperCase(),
                                      bezeichnung: t.bezeichnung,
                                      /* Ein Training, ein Betrag — nicht je Tag (0189). */
                                      einheit: 'einsatz',
                                      /* Hinter die Stundenzeilen, wie das Ladewerkzeug. */
                                      reihenfolge: 1000 + stand.leistungsarten.length * 10,
                                      bemerkung: `Honorareintrag zum Fahrtraining ${t.artikelnummer}.`,
                                      artikel: t.artikel,
                                    }),
                                  `„${t.bezeichnung}" steht jetzt in der Tabelle. Der Betrag fehlt noch.`,
                                );
                              }}
                            >
                              Eintrag anlegen
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {stand.ohneEintragGesamt > stand.ohneEintrag.length && (
                <p className="hilfe">
                  Weitere {String(stand.ohneEintragGesamt - stand.ohneEintrag.length)} sind hier
                  nicht aufgeführt. Für viele auf einmal gibt es das Werkzeug
                  <code> deploy/scripts/honorarkategorien.mjs</code>.
                </p>
              )}
            </div>
          )}
        </>
      }
    >
      {/* ==================================================================
          2. DAS GITTER: KATEGORIE MAL TARIF (RES-68, RES-72)
          ================================================================== */}
      <div className="karte">
        <div className="karte__titel">
          <h2>
            Honorarsätze
            {gefaessZeile !== undefined && (
              <>
                {' '}
                <span className="hilfe">
                  in <code>{gefaessZeile.kennung}</code>
                  {gefaessZeile.ressource !== null
                    && ` · ${gefaessZeile.ressourceName ?? 'ein Trainer'}`}
                </span>
              </>
            )}
          </h2>
          <small>
            Was das Haus je Fahrtraining zahlt, dazu die beiden Stundenzeilen. Am Wochenende
            gilt der Wochenendtarif; fehlt er, springt der Wochentagstarif ein. Die
            Stundenzeilen hängen an keinem Training und werden nach Stunden gezählt.
          </small>
        </div>

        <Honorargitter
          wirksam={wirksamAusSaetzen(stand, stichtag)}
          stichtag={stichtag}
          beiStichtag={setzeStichtag}
          tabelle={stand.gewaehlt ?? ''}
          stufe={gefaessZeile === undefined || gefaessZeile.ressource === null
            ? 'allgemein'
            : 'ressource'}
          darfAendern={darfAendern}
          laedt={laedt}
          beiHandgriff={anpacken}
          stichtagInWerkzeugleiste
          beiBearbeiten={setzeGitterOffen}
          {...(darfAendern
            ? {
                beiStilllegen: (schluessel: string, bezeichnung: string) => {
                  anpacken(
                    () => auswahlAendern('leistungsarten', schluessel, { aktiv: false }),
                    `„${bezeichnung}" wird nicht mehr angeboten. Was sie trägt, bleibt lesbar.`,
                  );
                },
              }
            : {})}
        />

        {/* ----------------------------------------------------------------
            DER WEG ZURÜCK — und er braucht keine zweite Tabelle.

            Eine stillgelegte Kategorie steht nicht im Gitter (dort stehen die
            angebotenen). Ohne diese Zeile wäre sie verschwunden, und
            „außer Gebrauch heißt nicht gelöscht" wäre eine Zusage ohne Weg. */}
        {stillgelegt.length > 0 && (
          <p className="feld__hilfe">
            Außer Gebrauch:{' '}
            {stillgelegt.map((a, i) => (
              <span key={a.schluessel}>
                {i > 0 && ', '}
                {a.bezeichnung}
                {darfAendern && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="knopf knopf--verweis"
                      disabled={laedt}
                      onClick={() => {
                        anpacken(
                          () => auswahlAendern('leistungsarten', a.schluessel, { aktiv: true }),
                          `„${a.bezeichnung}" wird wieder angeboten.`,
                        );
                      }}
                    >
                      wieder anbieten
                    </button>
                  </>
                )}
              </span>
            ))}
          </p>
        )}

        {/* ----------------------------------------------------------------
            EINE KATEGORIE OHNE TRAINING ANLEGEN.

            Der eine Fall, den das Gitter nicht von sich aus abdeckt: etwas, das
            KEIN Fahrtraining ist — eine dritte Stundenzeile etwa. Eine Kategorie
            ZU einem Training entsteht oben mit einem Knopf, und ihr Name kommt
            vom Produkt; hier von Hand einen zu tippen wäre der Weg, der beim
            nächsten Katalogzugang überschrieben wird. */}
        {darfAendern && (
          <>
            {eigene === null ? (
              <div className="knopfreihe">
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => {
                    setzeEigene({ schluessel: '', bezeichnung: '', einheit: 'stunde' });
                  }}
                >
                  Kategorie ohne Training anlegen
                </button>
              </div>
            ) : (
              <>
                <p className="feld__hilfe">
                  Für etwas, das kein Fahrtraining ist. Der <strong>Schlüssel</strong> steht in
                  Großbuchstaben und bleibt danach, wie er ist — er verbindet Sätze und
                  Aufträge.
                </p>
                <div className="feldreihe">
                  <div className="feld">
                    <label htmlFor="eig-schluessel">Schlüssel</label>
                    <input
                      id="eig-schluessel"
                      className="feld__eingabe feld__eingabe--schmal"
                      value={eigene.schluessel}
                      onChange={(e) => {
                        setzeEigene({ ...eigene, schluessel: e.target.value.toUpperCase() });
                      }}
                    />
                  </div>
                  <div className="feld">
                    <label htmlFor="eig-bezeichnung">Bezeichnung</label>
                    <input
                      id="eig-bezeichnung"
                      className="feld__eingabe"
                      value={eigene.bezeichnung}
                      onChange={(e) => {
                        setzeEigene({ ...eigene, bezeichnung: e.target.value });
                      }}
                    />
                  </div>
                  <div className="feld">
                    <label htmlFor="eig-einheit">Einheit</label>
                    <select
                      id="eig-einheit"
                      value={eigene.einheit}
                      onChange={(e) => {
                        setzeEigene({
                          ...eigene,
                          einheit: e.target.value as Leistungseinheit,
                        });
                      }}
                    >
                      {EINHEITEN.map((e) => (
                        <option key={e} value={e}>
                          {EINHEITSNAME[e]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="knopfreihe">
                  <button
                    type="button"
                    className="knopf"
                    onClick={() => {
                      setzeEigene(null);
                    }}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    className="knopf knopf--haupt"
                    disabled={
                      laedt
                      || eigene.schluessel.trim() === ''
                      || eigene.bezeichnung.trim() === ''
                    }
                    onClick={() => {
                      const wort = eigene.bezeichnung.trim();
                      anpacken(async () => {
                        const ergebnis = await auswahlAnlegen('leistungsarten', {
                          schluessel: eigene.schluessel.trim(),
                          bezeichnung: wort,
                          einheit: eigene.einheit,
                          /* Vor die Trainings, zu den übrigen Stundenzeilen. */
                          reihenfolge: 70 + stand.leistungsarten.length,
                          bemerkung: null,
                          artikel: null,
                        });
                        setzeEigene(null);
                        return ergebnis;
                      }, `„${wort}" steht jetzt in der Tabelle. Der Betrag fehlt noch.`);
                    }}
                  >
                    Anlegen
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ----------------------------------------------------------------
            JEDE EINZELNE ZEILE (RES-72)

            Eingeklappt, weil sie im Alltag nicht gebraucht wird — und trotzdem
            da, weil sie der Beweis der Anforderung ist: Was einmal galt, ist
            nachlesbar, Zelle für Zelle. Und sie trägt die zwei Handlungen, die
            an einer einzelnen Zeile hängen. */}
        {stand.saetze.length > 0 && (
          <>
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf knopf--verweis"
                aria-expanded={zellen}
                onClick={() => {
                  setzeZellen(!zellen);
                }}
              >
                {zellen
                  ? 'Einzelne Sätze ausblenden'
                  : `Jeden einzelnen Satz zeigen (${String(stand.saetze.length)})`}
              </button>
            </div>

            {zellen && (
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle" aria-label="Alle Fassungen der Honorarsätze">
                  <thead>
                    <tr>
                      <th scope="col">Kategorie</th>
                      <th scope="col">Tarif</th>
                      <th scope="col">Betrag</th>
                      <th scope="col">gültig ab</th>
                      <th scope="col">gültig bis</th>
                      <th scope="col">Stand</th>
                      {darfAendern && <th scope="col">Handlung</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {stand.saetze.map((s) => (
                      <tr key={s.id} className={s.gueltigBis === null ? undefined : 'liste__zeile--blass'}>
                        <th scope="row">{s.leistungsartName}</th>
                        <td>{TARIFNAME[s.tarif]}</td>
                        <td className="zahl">{alsEurosatz(s.betrag)}</td>
                        <td className="liste__einzeilig">{alsTag(s.gueltigAb)}</td>
                        <td className="liste__einzeilig">{alsTag(s.gueltigBis) ?? <span className="hilfe">offen</span>}</td>
                        <td>
                          {s.inGebrauch ? (
                            <span className="marke">in Aufträgen verwendet</span>
                          ) : (
                            <span className="hilfe">noch nicht verwendet</span>
                          )}
                        </td>
                        {darfAendern && (
                          <td>
                            {/*
                              ZWEI HANDLUNGEN, UND KEINE DAVON ÄNDERT DEN BETRAG.
                              „Schließen" setzt das Ende auf gestern, damit der
                              Nachfolger ab heute gilt — der Weg für den Fall, dass
                              ein Tarif ausläuft, ohne dass ein neuer kommt.
                              „Entfernen" gibt es nur, solange kein Auftrag darauf
                              steht; danach wäre es der Verlust der Herkunft
                              (RES-72).
                            */}
                            {s.gueltigBis === null && (
                              <button
                                type="button"
                                className="knopf knopf--klein"
                                disabled={laedt}
                                onClick={() => {
                                  anpacken(
                                    () => satzSchliessen(s.id, { gueltigBis: tagDavor(heute()) }),
                                    `Der Satz gilt bis ${tagDavor(heute())}.`,
                                  );
                                }}
                              >
                                Zum heutigen Tag schließen
                              </button>
                            )}
                            {!s.inGebrauch && (
                              <>
                                {' '}
                                <button
                                  type="button"
                                  className="knopf knopf--eingriff knopf--klein"
                                  disabled={laedt}
                                  onClick={() => {
                                    setzeNachfrage({ art: 'satz', satz: s });
                                  }}
                                >
                                  Entfernen …
                                </button>
                              </>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ----------------------------------------------------------------
          DIE FASSUNGEN DIESER TABELLE (RES-68, RES-72, 0190)

          Das Gegenstück zur Preislistenübersicht: ein Tag, ein Name, die Zahl
          der Sätze. Sie steht OFFEN und nicht eingeklappt — sie ist die
          Antwort auf „was gilt wann", und das ist im Alltag eine Frage und
          keine Nachforschung.

          Seit ÄA-41 steht sie als eigene Karte UNTER dem Gitter und nicht in
          einer Seitenleiste: Fünf Spalten mit zwei Handlungen passen nicht in
          360 px. Die Zellenliste bleibt beim Gitter, eingeklappt: Sie trägt
          die Handlungen an der EINZELNEN Zeile, und die braucht man selten. */}
      {stand.fassungen.length > 0 && (
        <div className="karte">
          <div className="karte__titel">
            <h2>Fassungen dieser Tabelle</h2>
            <small>{stand.fassungen.length}</small>
          </div>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Fassungen der Honorartabelle">
              <thead>
                <tr>
                  <th scope="col">gültig ab</th>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col">neu hinterlegt</th>
                  <th scope="col">Sätze insgesamt</th>
                  <th scope="col">Handlung</th>
                </tr>
              </thead>
              <tbody>
                {stand.fassungen.map((f) => {
                  const kuenftig = f.gueltigAb > heute();
                  return (
                    <tr
                      key={f.gueltigAb}
                      className={f.gueltigAb === stichtag ? 'liste__zeile--gewaehlt' : undefined}
                    >
                      <th scope="row">
                        {f.gueltigAb}
                        {/*
                          Eine Fassung, die noch nicht gilt, ist der Grund, warum
                          es diese Übersicht gibt: Ohne Marke sähe sie genauso aus
                          wie die geltende, und der nächste Leser nähme die Zahlen
                          von 2027 für die von heute.
                        */}
                        {kuenftig && (
                          <>
                            <br />
                            <span className="marke marke--warn">noch nicht in Kraft</span>
                          </>
                        )}
                      </th>
                      <td>
                        {f.bezeichnung ?? (
                          <span className="hilfe">ohne Bezeichnung</span>
                        )}
                      </td>
                      <td className="zahl">
                        {f.beginnend === 0 ? (
                          /*
                            Null heißt: An diesem Tag hat etwas AUFGEHÖRT, ohne
                            dass ein Nachfolger kam — eine geleerte Zelle. Auch
                            das ist eine Fassung der Tabelle, und ohne diese Zeile
                            wäre sie nicht anwählbar.
                          */
                          <span className="hilfe">— (hier endete etwas)</span>
                        ) : (
                          String(f.beginnend)
                        )}
                      </td>
                      <td className="zahl">{String(f.geltend)}</td>
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={f.gueltigAb === stichtag}
                          onClick={() => {
                            setzeMeldung(null);
                            setzeStichtag(f.gueltigAb);
                          }}
                        >
                          {f.gueltigAb === stichtag ? 'wird gezeigt' : 'anzeigen'}
                        </button>{' '}
                        {darfAendern && f.beginnend > 0 && (
                          <button
                            type="button"
                            className="knopf knopf--eingriff knopf--klein"
                            /*
                              GESPERRT, SOLANGE EIN AUFTRAG DARAUF STEHT (RES-72).
                              Der Server weist es ohnehin ab; die Sperre sagt es
                              vorher, statt den Anwender in eine Ablehnung laufen
                              zu lassen.
                            */
                            disabled={laedt || f.inGebrauch}
                            title={
                              f.inGebrauch
                                ? 'Auf dieser Fassung stehen Aufträge — sie bleibt.'
                                : undefined
                            }
                            onClick={() => {
                              setzeNachfrage({ art: 'fassung', fassung: f, tabelle: stand.gewaehlt! });
                            }}
                          >
                            Verwerfen …
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================================================================
          1. DIE HONORARTABELLEN — DAS GEFÄSS (RES-68, RES-69, 0191)
          ==================================================================

          Auftraggeber am 12.09.2026: „Die honorarpreisliste soll sich
          konfigurieren lassen, wie die produktpreisliste." Und: „Es soll immer
          nur eine gültige Preisliste geben, plus die Möglichkeit der
          individuellen Vereinbarungen mit den Trainern selbst."

          Das ist diese Karte — dieselben Spalten wie `Preislisten.tsx`, und
          zwei Dinge WENIGER: keine Rangstufe und kein „vorrangig". Die gibt es
          dort, weil mehrere Preislisten gleichzeitig gelten und eine gewinnen
          muss. Hier gilt je Stichtag genau eine, und damit ist nichts zu
          ordnen. */}
      <div className="karte">
        <div className="karte__titel">
          <h2>Honorartabellen</h2>
          <small>
            Es gilt immer genau <strong>eine</strong> allgemeine Tabelle, dazu je Trainer
            höchstens eine eigene Vereinbarung. Die Datenbank lässt keine zweite
            zur selben Zeit zu.
          </small>
        </div>

        <div className="liste__leiste">
          <label className="liste__filter">
            <input
              type="checkbox"
              checked={auchBeendete}
              onChange={(e) => {
                setzeAuchBeendete(e.target.checked);
              }}
            />{' '}
            auch beendete
          </label>{' '}
          {darfAendern && (
            <button
              className="knopf knopf--klein"
              type="button"
              onClick={() => {
                setzeMeldung(null);
                setzeGefaess({
                  art: 'neu',
                  id: '',
                  kennung: '',
                  bezeichnung: '',
                  gueltigAb: heute(),
                  gueltigBis: '',
                  bemerkung: '',
                  uebernehmen: false,
                  /* Eine neue Tabelle ist die allgemeine; eine Vereinbarung entsteht
                     in der Ressourcenakte oder als Kopie. */
                  ressource: null,
                });
              }}
            >
              Honorartabelle anlegen
            </button>
          )}
        </div>

        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" aria-label="Honorartabellen">
            <thead>
              <tr>
                <th scope="col">Kennung</th>
                <th scope="col">Bezeichnung</th>
                <th scope="col">Gilt für</th>
                <th scope="col">Gültig</th>
                <th scope="col">Sätze</th>
                <th scope="col">Handlung</th>
              </tr>
            </thead>
            <tbody>
              {sichtbareTabellen.map((t) => {
                const beendet = t.gueltigBis !== null && t.gueltigBis < heute();
                return (
                  <tr
                    key={t.id}
                    className={
                      t.id === stand.gewaehlt
                        ? 'liste__zeile--gewaehlt'
                        : beendet || t.geloeschtAm !== null
                          ? 'liste__zeile--blass'
                          : undefined
                    }
                  >
                    <th scope="row" className="liste__nummer">
                      <code>{t.kennung}</code>
                      {/*
                        DREI ZUSTÄNDE, DIE MAN SEHEN MUSS. „gilt" ist die
                        Auskunft, die im Alltag gebraucht wird; „noch nicht in
                        Kraft" verhindert, dass eine vorbereitete Tabelle für die
                        geltende genommen wird; „gelöscht" sagt, warum eine
                        Tabelle blass ist, obwohl ihr Zeitraum passt.
                      */}
                      {t.gilt && (
                        <>
                          <br />
                          <span className="marke">gilt</span>
                        </>
                      )}
                      {!t.gilt && t.geloeschtAm === null && t.gueltigAb > heute() && (
                        <>
                          <br />
                          <span className="marke marke--warn">noch nicht in Kraft</span>
                        </>
                      )}
                      {t.geloeschtAm !== null && (
                        <>
                          <br />
                          <span className="marke marke--inaktiv">gelöscht</span>
                        </>
                      )}
                    </th>
                    <td>{t.bezeichnung}</td>
                    <td>
                      {t.ressource === null ? (
                        <span className="hilfe">alle Trainer (allgemeine Vorgabe)</span>
                      ) : (
                        (t.ressourceName ?? 'ein Trainer')
                      )}
                    </td>
                    <td>
                      <span className="liste__einzeilig">ab {alsTag(t.gueltigAb)}</span>
                      {t.gueltigBis === null ? (
                        <span className="hilfe"> · unbefristet</span>
                      ) : (
                        <>
                          {' '}
                          <span className="liste__einzeilig">bis {alsTag(t.gueltigBis)}</span>
                        </>
                      )}
                    </td>
                    <td className="zahl">{String(t.saetze)}</td>
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        disabled={t.id === stand.gewaehlt || laedt}
                        onClick={() => {
                          zeigeTabelle(t.id, t.gueltigAb);
                        }}
                      >
                        {t.id === stand.gewaehlt ? 'wird gezeigt' : 'Sätze zeigen'}
                      </button>{' '}
                      {darfAendern && t.geloeschtAm === null && (
                        <>
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() => {
                              setzeMeldung(null);
                              setzeGefaess({
                                art: 'aendern',
                                id: t.id,
                                kennung: t.kennung,
                                bezeichnung: t.bezeichnung,
                                gueltigAb: t.gueltigAb,
                                gueltigBis: t.gueltigBis ?? '',
                                bemerkung: t.bemerkung ?? '',
                                uebernehmen: false,
                                ressource: t.ressource,
                              });
                            }}
                          >
                            Bearbeiten
                          </button>{' '}
                          {/*
                            DIE NACHFOLGERIN — der Weg, den „nur eine gültige"
                            nötig macht. Ohne ihn müsste der Anwender erst das
                            Gültig-bis setzen und dann anlegen; in der falschen
                            Reihenfolge läuft er in eine Ablehnung, deren Ursache
                            eine Reihenfolge war.
                          */}
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            disabled={t.gueltigBis !== null && t.gueltigBis < heute()}
                            onClick={() => {
                              setzeMeldung(null);
                              setzeGefaess({
                                art: 'nachfolger',
                                id: t.id,
                                kennung: '',
                                bezeichnung: '',
                                gueltigAb: '',
                                gueltigBis: '',
                                bemerkung: '',
                                /* Vorbelegt: Der Regelfall ist eine Tarifrunde, keine leere Tabelle. */
                                uebernehmen: true,
                                ressource: t.ressource,
                              });
                            }}
                          >
                            Nachfolgerin anlegen
                          </button>{' '}
                          {/*
                            KOPIEREN (0192) — „honorartabellen sollen kopierbar
                            sein". Der Fall dahinter: Ein Trainer soll die
                            Haussätze bekommen und in zwei Zellen etwas anderes.
  
                            Die Trainerliste wird ERST HIER geladen; die Maske
                            braucht sie sonst nie.
                          */}
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() => {
                              setzeMeldung(null);
                              setzeGefaess({
                                art: 'kopie',
                                id: t.id,
                                kennung: '',
                                bezeichnung: `${t.bezeichnung} (Kopie)`,
                                gueltigAb: heute(),
                                gueltigBis: '',
                                bemerkung: '',
                                uebernehmen: true,
                                /* Vorbelegt wie die Vorlage — der Rest ist eine Wahl. */
                                ressource: t.ressource,
                              });
                              if (trainer === null) {
                                void ladeTrainer()
                                  .then(setzeTrainer)
                                  /*
                                    Scheitert das Laden, bleibt die Liste leer und
                                    die Maske sagt es im Formular. Ein Fehler an
                                    dieser Stelle darf das Kopieren an die
                                    allgemeine Stufe nicht verhindern.
                                  */
                                  .catch(() => setzeTrainer([]));
                              }
                            }}
                          >
                            Kopieren
                          </button>{' '}
                          {/*
                            GELÖSCHT WIRD NUR EINE LEERE. Der Server weist es
                            ohnehin ab (`honorartabelle_nicht_leer`); die Sperre
                            sagt es vorher und nennt in der Beschriftung den
                            richtigen Weg.
                          */}
                          <button
                            type="button"
                            className="knopf knopf--eingriff knopf--klein"
                            disabled={laedt || t.saetze > 0 || t.zuschlaege > 0}
                            title={
                              t.saetze > 0 || t.zuschlaege > 0
                                ? 'In dieser Tabelle stehen Sätze — beenden statt löschen.'
                                : undefined
                            }
                            onClick={() => {
                              setzeNachfrage({ art: 'tabelle', tabelle: t });
                            }}
                          >
                            Löschen …
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sichtbareTabellen.length === 0 && (
          <p className="liste__leer">
            Keine Honorartabelle vorhanden. Ohne sie gibt es keinen Ort für einen Satz, und
            jede Anfrage verlangt eine ausdrückliche Angabe mit Grund.
          </p>
        )}

        {darfAendern && gefaess !== null && (
          <Gefaesspflege
            entwurf={gefaess}
            vorgaenger={stand.tabellen.find((t) => t.id === gefaess.id)}
            trainer={trainer}
            laedt={laedt}
            beiAbbrechen={() => {
              setzeGefaess(null);
            }}
            beiSpeichern={(fertig) => {
              anpacken(async () => {
                if (fertig.art === 'neu') {
                  const { id } = await tabelleAnlegen({
                    kennung: fertig.kennung.trim(),
                    bezeichnung: fertig.bezeichnung.trim(),
                    /*
                      ALLGEMEIN UND NICHT AN EINEM TRAINER. Eine individuelle
                      Vereinbarung entsteht in der Ressourcenakte — dort steht
                      der Trainer, um den es geht, und nur dort ist die Frage
                      „für wen?" schon beantwortet.
                    */
                    ressource: null,
                    gueltigAb: fertig.gueltigAb,
                    gueltigBis: fertig.gueltigBis === '' ? null : fertig.gueltigBis,
                    bemerkung: fertig.bemerkung.trim() === '' ? null : fertig.bemerkung.trim(),
                  });
                  setzeGefaess(null);
                  setzeGewaehlt(id);
                  return { id };
                }
                if (fertig.art === 'kopie') {
                  const ergebnis = await tabelleKopieren({
                    vorlage: fertig.id,
                    kennung: fertig.kennung.trim(),
                    bezeichnung: fertig.bezeichnung.trim(),
                    ressource: fertig.ressource,
                    gueltigAb: fertig.gueltigAb,
                    gueltigBis: fertig.gueltigBis === '' ? null : fertig.gueltigBis,
                    bemerkung: fertig.bemerkung.trim() === '' ? null : fertig.bemerkung.trim(),
                    /*
                      Kein eigener Stichtag: Übernommen wird, was am Beginn der
                      Kopie gilt. Das ist die Frage, die man beim Kopieren stellt
                      — „was gilt dann?" und nicht „was gilt heute?".
                    */
                    stichtag: null,
                  });
                  setzeGefaess(null);
                  setzeGewaehlt(ergebnis.id);
                  setzeStichtag(fertig.gueltigAb);
                  return ergebnis;
                }
                if (fertig.art === 'nachfolger') {
                  const ergebnis = await nachfolgerAnlegen({
                    vorgaenger: fertig.id,
                    kennung: fertig.kennung.trim(),
                    bezeichnung: fertig.bezeichnung.trim(),
                    gueltigAb: fertig.gueltigAb,
                    bemerkung: fertig.bemerkung.trim() === '' ? null : fertig.bemerkung.trim(),
                    uebernehmen: fertig.uebernehmen,
                  });
                  setzeGefaess(null);
                  setzeGewaehlt(ergebnis.id);
                  setzeStichtag(fertig.gueltigAb);
                  return ergebnis;
                }
                const ergebnis = await tabelleAendern(fertig.id, {
                  bezeichnung: fertig.bezeichnung.trim(),
                  gueltigAb: fertig.gueltigAb,
                  gueltigBis: fertig.gueltigBis === '' ? null : fertig.gueltigBis,
                  bemerkung: fertig.bemerkung.trim() === '' ? null : fertig.bemerkung.trim(),
                });
                setzeGefaess(null);
                return ergebnis;
              }, gefaess.art === 'aendern'
                ? 'Die Honorartabelle ist geändert.'
                : gefaess.art === 'nachfolger'
                  ? 'Die Nachfolgerin steht, die Vorgängerin endet am Tag davor.'
                  : gefaess.art === 'kopie'
                    ? 'Die Kopie steht. Sie ist von der Vorlage unabhängig — eine '
                      + 'geleerte Zelle nimmt wieder die Standardtabelle.'
                    : 'Die Honorartabelle ist angelegt.');
            }}
          />
        )}
      </div>

      {/* ==================================================================
          3. ZUSCHLÄGE UND NEBENKOSTEN (RES-71)
          ==================================================================

          EINE TABELLE UND NICHT EINE JE TRAININGSTYP: Ein Kilometergeld ist
          fast immer bei allen gleich, und die Frage im Alltag lautet „wo
          weicht etwas ab?". Untereinander sieht man das, in vier Karten
          nicht. */}
      <div className="karte">
        <div className="karte__titel">
          <h2>Zuschläge und Nebenkosten</h2>
          <small>
            Was zu einem Satz hinzukommt. Der <strong>Wochenendzuschlag</strong> gilt
            nur ersatzweise: Steht für die Kategorie ein Wochenendtarif, steckt das
            Wochenende schon im Satz, und der Zuschlag wird nicht angeboten. Am 12.09.2026
            wurden alle Zuschlagsarten entfernt — die Möglichkeit bleibt, die Liste ist leer.
          </small>
        </div>

        {stand.zuschlaege.length === 0 ? (
          <p className="liste__leer">Kein Zuschlag hinterlegt.</p>
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Zuschläge und Nebenkosten">
              <thead>
                <tr>
                  <th scope="col">Kategorie</th>
                  <th scope="col">Art</th>
                  <th scope="col">Berechnung</th>
                  <th scope="col">Wert</th>
                  <th scope="col">gültig ab</th>
                  {darfAendern && <th scope="col">Handlung</th>}
                </tr>
              </thead>
              <tbody>
                {stand.zuschlaege.map((z) => (
                  <tr key={z.id}>
                    <th scope="row">{z.leistungsartName}</th>
                    <td>{z.zuschlagsartName}</td>
                    <td>{BERECHNUNGSNAME[z.berechnung]}</td>
                    <td className="zahl">
                      {z.berechnung === 'prozent'
                        ? alsProzent(z.wert)
                        : alsEurosatz(z.wert)}
                    </td>
                    <td className="liste__einzeilig">{alsTag(z.gueltigAb)}</td>
                    {darfAendern && (
                      <td>
                        {z.inGebrauch ? (
                          <span className="hilfe">in Aufträgen verwendet</span>
                        ) : (
                          <button
                            type="button"
                            className="knopf knopf--eingriff knopf--klein"
                            disabled={laedt}
                            onClick={() => {
                              setzeNachfrage({ art: 'zuschlag', zuschlag: z });
                            }}
                          >
                            Entfernen …
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {darfAendern && zuschlag === null && aktiveArten.length > 0 && (
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf knopf--klein"
              onClick={() => {
                setzeZuschlag({
                  leistungsart: aktiveArten[0]?.schluessel ?? '',
                  zuschlagsart: stand.zuschlagsarten[0]?.schluessel ?? '',
                  berechnung: stand.zuschlagsarten[0]?.einheit != null ? 'je_einheit' : 'prozent',
                  wert: '',
                  gueltigAb: heute(),
                });
              }}
            >
              Zuschlag anlegen
            </button>
          </div>
        )}

        {darfAendern && zuschlag !== null && (
          <>
            <div className="feldreihe">
              <div className="feld">
                <label htmlFor="z-art">Kategorie</label>
                <select
                  id="z-art"
                  value={zuschlag.leistungsart}
                  onChange={(e) => {
                    setzeZuschlag({ ...zuschlag, leistungsart: e.target.value });
                  }}
                >
                  {aktiveArten.map((a) => (
                    <option key={a.schluessel} value={a.schluessel}>
                      {a.bezeichnung}
                    </option>
                  ))}
                </select>
              </div>
              <div className="feld">
                <label htmlFor="z-zart">Zuschlagsart</label>
                <select
                  id="z-zart"
                  value={zuschlag.zuschlagsart}
                  onChange={(e) => {
                    const gewaehlt = stand.zuschlagsarten.find(
                      (a) => a.schluessel === e.target.value,
                    );
                    setzeZuschlag({
                      ...zuschlag,
                      zuschlagsart: e.target.value,
                      /*
                        Eine Art mit Einheit wird je Einheit gerechnet — die
                        Datenbank lässt nichts anderes zu (0183). Die Maske stellt
                        es gleich richtig ein, statt den Anwender in eine
                        Ablehnung laufen zu lassen.
                      */
                      berechnung:
                        gewaehlt?.einheit != null ? 'je_einheit' : zuschlag.berechnung,
                    });
                  }}
                >
                  {stand.zuschlagsarten
                    .filter((a) => a.aktiv)
                    .map((a) => (
                      <option key={a.schluessel} value={a.schluessel}>
                        {a.bezeichnung}
                      </option>
                    ))}
                </select>
              </div>
              <div className="feld">
                <label htmlFor="z-berechnung">Berechnung</label>
                <select
                  id="z-berechnung"
                  value={zuschlag.berechnung}
                  disabled={
                    stand.zuschlagsarten.find((a) => a.schluessel === zuschlag.zuschlagsart)
                      ?.einheit != null
                  }
                  onChange={(e) => {
                    setzeZuschlag({ ...zuschlag, berechnung: e.target.value as Berechnung });
                  }}
                >
                  {(['prozent', 'pauschale', 'je_einheit'] as const).map((b) => (
                    <option key={b} value={b}>
                      {BERECHNUNGSNAME[b]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="feld">
                <label htmlFor="z-wert">
                  {zuschlag.berechnung === 'prozent' ? 'Prozent' : 'Betrag (netto)'}
                </label>
                <input
                  id="z-wert"
                  className="feld__eingabe feld__eingabe--schmal"
                  inputMode="decimal"
                  value={zuschlag.wert}
                  onChange={(e) => {
                    setzeZuschlag({ ...zuschlag, wert: e.target.value });
                  }}
                />
              </div>
            </div>
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf"
                onClick={() => {
                  setzeZuschlag(null);
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="knopf knopf--haupt"
                disabled={laedt || zuschlag.wert.trim() === ''}
                onClick={() => {
                  anpacken(async () => {
                    const ergebnis = await zuschlagAnlegen({
                      leistungsart: zuschlag.leistungsart,
                      zuschlagsart: zuschlag.zuschlagsart,
                      tabelle: stand.gewaehlt!,
                      berechnung: zuschlag.berechnung,
                      wert: zuschlag.wert.trim(),
                      gueltigAb: zuschlag.gueltigAb,
                      gueltigBis: null,
                      bemerkung: null,
                    });
                    setzeZuschlag(null);
                    return ergebnis;
                  }, 'Der Zuschlag ist hinterlegt.');
                }}
              >
                Speichern
              </button>
            </div>
          </>
        )}
      </div>

      {/* ==================================================================
          4. DIE ZUSCHLAGSARTEN (RES-71, SYS-10)
          ================================================================== */}
      <Auswahllistenpflege
        liste="zuschlagsarten"
        titel="Zuschlagsarten"
        einzahl="Zuschlagsart"
        erklaerung={
          <>
            Was es überhaupt an Zuschlägen geben kann. Eine Art mit Einheit — Kilometer,
            Nacht — wird je Einheit gerechnet; ohne Einheit entscheidet der einzelne
            Zuschlag, ob er prozentual oder pauschal ist.
          </>
        }
        zeilen={stand.zuschlagsarten}
        mitEinheit
        darfAendern={darfAendern}
        laedt={laedt}
        beiHandgriff={anpacken}
      />

      {/* ==================================================================
          5. DIE GRÜNDE DER ÜBERSTEUERUNG (RES-70)
          ================================================================== */}
      {/* RES-75 */}
      <Auswahllistenpflege
        liste="uebersteuerungsgruende"
        titel="Gründe für eine Übersteuerung"
        einzahl="Grund"
        erklaerung={
          <>
            Wer bei einer Anfrage vom Tabellensatz abweicht, wählt einen dieser Gründe. Ein
            Freitext wäre nicht auswertbar.
          </>
        }
        zeilen={stand.uebersteuerungsgruende}
        mitEinheit={false}
        darfAendern={darfAendern}
        laedt={laedt}
        beiHandgriff={anpacken}
      />

      {nachfrage !== null && nachfrageFenster(nachfrage)}
    </Planung>
  );

  /**
   * DIE RÜCKFRAGE VOR DEM LÖSCHEN (CRM-104, RES-72).
   *
   * Vier Wege, etwas Gespeichertes ganz wegzunehmen — einen Satz, eine
   * Fassung, eine leere Tabelle, einen Zuschlag. Jeder ist nur offen, solange
   * kein Auftrag daran hängt; zurückholen lässt sich danach keiner, nur neu
   * eintragen. „Zum heutigen Tag schließen" fragt nicht: Es beendet nur, und
   * ein neuer Betrag im Gitter füllt die Zelle wieder.
   */
  function nachfrageFenster(n: Nachfrage): JSX.Element {
    const schliessen = (): void => {
      setzeNachfrage(null);
    };
    switch (n.art) {
      case 'satz': {
        const s = n.satz;
        return (
          <Rueckfrage
            frage="Satz entfernen?"
            unterzeile={`${s.leistungsartName} · ${TARIFNAME[s.tarif]}`}
            tat="Entfernen"
            beiBestaetigen={() => schreibe(() => satzLoeschen(s.id), 'Der Satz ist entfernt.')}
            beiSchliessen={schliessen}
          >
            <p>
              Der Satz über {alsEurosatz(s.betrag)} ab {alsTag(s.gueltigAb)} wird gelöscht.
              Noch steht kein Auftrag darauf; zurück kommt er nur, indem man ihn neu einträgt.
            </p>
          </Rueckfrage>
        );
      }
      case 'fassung': {
        const f = n.fassung;
        return (
          <Rueckfrage
            frage="Fassung verwerfen?"
            unterzeile={`ab ${alsTag(f.gueltigAb) ?? f.gueltigAb}${f.bezeichnung === null ? '' : ` · ${f.bezeichnung}`}`}
            tat="Verwerfen"
            beiBestaetigen={() =>
              schreibe(async () => {
                await fassungVerwerfen(f.gueltigAb, n.tabelle);
                /*
                  Der Stichtag darf nicht auf einer Fassung stehenbleiben, die
                  es nicht mehr gibt — sonst zeigt das Gitter danach das Loch,
                  das gerade geschlossen wurde.
                */
                if (stichtag === f.gueltigAb) setzeStichtag(heute());
              }, `Die Fassung ab ${f.gueltigAb} ist verworfen. Die vorherigen Sätze gelten wieder.`)
            }
            beiSchliessen={schliessen}
          >
            <p>
              {f.beginnend === 1
                ? 'Der Satz, der an diesem Tag neu hinterlegt ist, wird gelöscht.'
                : `Die ${String(f.beginnend)} Sätze, die an diesem Tag neu hinterlegt sind, werden gelöscht.`}{' '}
              Die Sätze davor gelten wieder bis zu ihrem alten Ende.
            </p>
          </Rueckfrage>
        );
      }
      case 'tabelle': {
        const t = n.tabelle;
        return (
          <Rueckfrage
            frage="Honorartabelle löschen?"
            unterzeile={`${t.kennung} · ${t.bezeichnung}`}
            tat="Löschen"
            beiBestaetigen={() =>
              schreibe(async () => {
                await tabelleLoeschen(t.id);
                if (gewaehlt === t.id) setzeGewaehlt(null);
              }, `Die Honorartabelle ${t.kennung} ist gelöscht.`)
            }
            beiSchliessen={schliessen}
          >
            <p>
              Die Tabelle ist leer. Sie verschwindet aus der Auswahl und bleibt in der Liste als
              gelöscht stehen; zurückholen lässt sie sich nicht.
            </p>
          </Rueckfrage>
        );
      }
      case 'zuschlag': {
        const z = n.zuschlag;
        return (
          <Rueckfrage
            frage="Zuschlag entfernen?"
            unterzeile={`${z.leistungsartName} · ${z.zuschlagsartName}`}
            tat="Entfernen"
            beiBestaetigen={() =>
              schreibe(() => zuschlagLoeschen(z.id), 'Der Zuschlag ist entfernt.')
            }
            beiSchliessen={schliessen}
          >
            <p>
              Der Zuschlag über{' '}
              {z.berechnung === 'prozent' ? alsProzent(z.wert) : alsEurosatz(z.wert)} ab {alsTag(z.gueltigAb)} wird gelöscht.
              Noch steht kein Auftrag darauf; zurück kommt er nur, indem man ihn neu anlegt.
            </p>
          </Rueckfrage>
        );
      }
    }
  }
}

/**
 * Das Formular einer Honorartabelle (RES-68, 0191).
 *
 * ============================================================================
 * DREI WEGE IN EINEM FORMULAR, UND EINER DAVON IST NICHT DAS ÄNDERN
 * ============================================================================
 *
 * `neu` legt eine allgemeine Tabelle an. `aendern` fasst Bezeichnung, Zeitraum
 * und Bemerkung an — **nicht die Kennung**: Sie steht in jeder Auskunft darüber,
 * woher ein Satz kam, und eine umbenannte macht eine alte Herleitung unlesbar.
 * Dieselbe Regel wie bei der Preisliste (PRF-13).
 *
 * `nachfolger` ist der Weg, den „es soll immer nur eine gültige geben" nötig
 * macht: Er beendet die Vorgängerin zum Tag davor und legt die Neue an, in einer
 * Klammer. Wer das in zwei Schritten macht und die Reihenfolge vertauscht, läuft
 * in eine Ablehnung, deren Ursache eine Reihenfolge war.
 *
 * DAS ÜBERNEHMEN IST VORBELEGT. Der Regelfall einer neuen Tabelle ist eine
 * Tarifrunde — dieselben Zeilen, andere Zahlen. Eine leere Tabelle zum
 * Jahreswechsel wäre der Zustand, in dem jede Anfrage eine Übersteuerung
 * verlangt (RES-70), und das merkt man erst am 2. Januar.
 */
function Gefaesspflege({
  entwurf,
  vorgaenger,
  trainer,
  laedt,
  beiSpeichern,
  beiAbbrechen,
}: {
  readonly entwurf: Gefaessentwurf;
  /** Bei „nachfolger" und „kopie": die Vorlage — für den Hinweis darunter. */
  readonly vorgaenger: Honorartabellenzeile | undefined;
  /** Die externen Trainer; `null` heißt „wird noch geladen" (0192). */
  readonly trainer: readonly Trainerwahl[] | null;
  readonly laedt: boolean;
  readonly beiSpeichern: (fertig: Gefaessentwurf) => void;
  readonly beiAbbrechen: () => void;
}): JSX.Element {
  const [formular, setzeFormular] = useState(entwurf);
  const nachfolger = formular.art === 'nachfolger';
  const kopie = formular.art === 'kopie';
  const neu = formular.art === 'neu';

  /** Der Tag davor — für den Hinweis, wann die Vorgängerin endet. */
  const davor =
    formular.gueltigAb === ''
      ? ''
      : (() => {
          const gelesen = new Date(`${formular.gueltigAb}T12:00:00Z`);
          gelesen.setUTCDate(gelesen.getUTCDate() - 1);
          return gelesen.toISOString().slice(0, 10);
        })();

  const vollstaendig =
    formular.bezeichnung.trim() !== ''
    && formular.gueltigAb !== ''
    && (formular.art === 'aendern' ? true : formular.kennung.trim() !== '');

  return (
    <fieldset>
      <legend>
        {neu
          ? 'Neue Honorartabelle'
          : nachfolger
            ? `Nachfolgerin von ${vorgaenger?.kennung ?? ''}`
            : kopie
              ? `Kopie von ${vorgaenger?.kennung ?? ''}`
              : `Honorartabelle ${formular.kennung}`}
      </legend>

      {kopie && (
        <p className="feld__hilfe">
          Übernommen wird, was in <code>{vorgaenger?.kennung ?? 'der Vorlage'}</code> am
          Beginn der Kopie gilt — <strong>ein Stand, keine Geschichte</strong>. Danach sind
          beide Tabellen unabhängig: Eine Änderung an der Vorlage wirkt nicht mehr auf die
          Kopie. Wer will, dass die Standardtabelle weiter durchschlägt, lässt die Zelle{' '}
          <strong>leer</strong>.
        </p>
      )}

      <div className="feldreihe">
        {formular.art !== 'aendern' ? (
          <div className="feld">
            <label htmlFor="gf-kennung">Kennung</label>
            <input
              id="gf-kennung"
              className="feld__eingabe feld__eingabe--schmal"
              value={formular.kennung}
              placeholder="HONORARE-2027"
              onChange={(e) => {
                setzeFormular({ ...formular, kennung: e.target.value.toUpperCase() });
              }}
            />
            <p className="feld__hilfe">
              Großbuchstaben, Ziffern, Bindestrich. Danach <strong>nicht mehr änderbar</strong>:
              Sie steht in jeder Auskunft darüber, woher ein Satz kam.
            </p>
          </div>
        ) : (
          <div className="feld">
            <span className="feld__beschriftung">Kennung</span>
            <p>
              <code>{formular.kennung}</code>
            </p>
          </div>
        )}
        <div className="feld">
          <label htmlFor="gf-bezeichnung">Bezeichnung</label>
          <input
            id="gf-bezeichnung"
            className="feld__eingabe"
            value={formular.bezeichnung}
            placeholder="Honorare 2027"
            onChange={(e) => {
              setzeFormular({ ...formular, bezeichnung: e.target.value });
            }}
          />
        </div>
      </div>

      <div className="feldreihe">
        <div className="feld">
          <label htmlFor="gf-ab">Gültig ab</label>
          <input
            id="gf-ab"
            type="date"
            className="feld__eingabe feld__eingabe--schmal"
            value={formular.gueltigAb}
            onChange={(e) => {
              setzeFormular({ ...formular, gueltigAb: e.target.value });
            }}
          />
          {nachfolger && davor !== '' && (
            <p className="feld__hilfe">
              <span className="marke marke--warn">
                {vorgaenger?.kennung ?? 'Die Vorgängerin'} endet damit am {davor}
              </span>{' '}
              Beides geschieht in einem Zug — es entsteht keine Lücke und keine
              Überschneidung.
            </p>
          )}
        </div>
        {!nachfolger && (
          <div className="feld">
            <label htmlFor="gf-bis">Gültig bis</label>
            <input
              id="gf-bis"
              type="date"
              className="feld__eingabe feld__eingabe--schmal"
              value={formular.gueltigBis}
              onChange={(e) => {
                setzeFormular({ ...formular, gueltigBis: e.target.value });
              }}
            />
            <p className="feld__hilfe">
              Leer heißt unbefristet. Eine Tabelle mit Sätzen wird <strong>beendet</strong> und
              nicht gelöscht — sie bleibt in jeder alten Herleitung nachvollziehbar.
            </p>
          </div>
        )}
      </div>

      {kopie && (
        <div className="feld">
          <span className="feld__beschriftung">Gilt für</span>
          {/*
            DIE EIGENTLICHE FRAGE BEIM KOPIEREN. Beim Nachfolger gibt es sie
            nicht — er behält die Stufe der Vorgängerin. Hier ist sie der Grund,
            warum kopiert wird: „die Haussätze für Herrn Behrens."
          */}
          <label htmlFor="gf-fueralle">
            <input
              id="gf-fueralle"
              type="radio"
              name="gf-ziel"
              checked={formular.ressource === null}
              onChange={() => setzeFormular({ ...formular, ressource: null })}
            />{' '}
            alle Trainer (allgemeine Vorgabe)
          </label>{' '}
          <label htmlFor="gf-fuereinen">
            <input
              id="gf-fuereinen"
              type="radio"
              name="gf-ziel"
              checked={formular.ressource !== null}
              disabled={trainer !== null && trainer.length === 0}
              onChange={() =>
                setzeFormular({ ...formular, ressource: trainer?.[0]?.id ?? null })
              }
            />{' '}
            einen einzelnen Trainer
          </label>

          {formular.ressource !== null && (
            <>
              <select
                className="feld__eingabe"
                aria-label="Trainer"
                value={formular.ressource}
                onChange={(e) => {
                  setzeFormular({ ...formular, ressource: e.target.value });
                }}
              >
                {(trainer ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.bezeichnung}
                  </option>
                ))}
              </select>
              <p className="feld__hilfe">
                Nur <strong>externe</strong> Trainer: Ein interner hat einen Arbeitsvertrag
                und kein Honorar. Gilt für ihn im gewählten Zeitraum schon eine
                Vereinbarung, wird die Kopie abgewiesen — es gilt immer nur eine.
              </p>
            </>
          )}
          {trainer === null && <p className="feld__hilfe">Trainer werden geladen …</p>}
          {trainer !== null && trainer.length === 0 && (
            <p className="feld__hilfe">
              Es ist kein externer Trainer vorhanden — die Kopie kann nur die allgemeine
              Vorgabe sein.
            </p>
          )}
        </div>
      )}

      {nachfolger && (
        <div className="feld">
          <label htmlFor="gf-uebernehmen">
            <input
              id="gf-uebernehmen"
              type="checkbox"
              checked={formular.uebernehmen}
              onChange={(e) => {
                setzeFormular({ ...formular, uebernehmen: e.target.checked });
              }}
            />{' '}
            Die geltenden Beträge übernehmen
          </label>
          <p className="feld__hilfe">
            Übernommen wird, was am Übergangstag gilt — nicht alles, was je in der
            Vorgängerin stand. Ohne Häkchen beginnt die neue Tabelle leer, und jede Anfrage
            verlangt dann eine ausdrückliche Angabe mit Grund.
          </p>
        </div>
      )}

      <div className="feld">
        <label htmlFor="gf-bemerkung">Bemerkung</label>
        <input
          id="gf-bemerkung"
          className="feld__eingabe"
          value={formular.bemerkung}
          onChange={(e) => {
            setzeFormular({ ...formular, bemerkung: e.target.value });
          }}
        />
      </div>

      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={beiAbbrechen}>
          Abbrechen
        </button>
        <button
          type="button"
          className="knopf knopf--haupt"
          disabled={laedt || !vollstaendig}
          onClick={() => {
            beiSpeichern(formular);
          }}
        >
          {/*
            EIGENE BESCHRIFTUNG FÜR DEN ÜBERGANG, und nicht dieselbe wie am Knopf
            in der Zeile: Sie sagt, dass ZWEI Dinge geschehen. „Nachfolgerin
            anlegen" verschweigt, dass die Vorgängerin dabei endet — und das ist
            die Hälfte, die man nicht übersieht haben will.
          */}
          {neu
            ? 'Anlegen'
            : nachfolger
              ? 'Anlegen und Vorgängerin beenden'
              : kopie
                ? 'Kopie anlegen'
                : 'Speichern'}
        </button>
      </div>
    </fieldset>
  );
}
